import { MergedDocumentPayloadDto } from "../../api-client";
import { BillPdfJobResult } from "../../hooks/useBatchPrintPdf";

// ============================================================================
// Types
// ============================================================================

/**
 * Common shape for merge document request.
 */
export interface MergeDocumentIds {
  paperDocumentIds: string[];
  billDocumentIds: string[];
}

/**
 * Response shape with dual-document fields (GKV/Copayment flows).
 */
export interface DualDocPrintResponse {
  paperCenterReceipt?: { id?: string } | null;
  billPdf?: { id?: string } | null;
}

/**
 * Response shape with single document field (Paper bill flow).
 */
export interface SingleDocPrintResponse {
  docDto?: { id?: string } | null;
}

// ============================================================================
// Extraction functions - convert flow-specific responses to common shape
// ============================================================================

/**
 * Extract document IDs from GKV/Copayment/Correction print responses.
 * These flows produce two documents: paperCenterReceipt + billPdf.
 */
export function extractDocIdsFromDualDocResponses(
  responses: DualDocPrintResponse[],
): MergeDocumentIds {
  const paperDocumentIds: string[] = [];
  const billDocumentIds: string[] = [];

  for (const response of responses) {
    if (response?.paperCenterReceipt?.id) {
      paperDocumentIds.push(response.paperCenterReceipt.id);
    }
    if (response?.billPdf?.id) {
      billDocumentIds.push(response.billPdf.id);
    }
  }

  return { paperDocumentIds, billDocumentIds };
}

/**
 * Extract document IDs from Paper bill print responses.
 * These flows produce a single document: docDto.
 */
export function extractDocIdsFromSingleDocResponses(
  responses: SingleDocPrintResponse[],
): MergeDocumentIds {
  const billDocumentIds: string[] = [];

  for (const response of responses) {
    if (response?.docDto?.id) {
      billDocumentIds.push(response.docDto.id);
    }
  }

  return { paperDocumentIds: [], billDocumentIds };
}

/**
 * Extract document IDs from batch workflow results.
 * Handles both dual-doc (GKV) and single-doc (Paper) result shapes.
 */
export function extractDocIdsFromBatchResults(results: BillPdfJobResult[]): MergeDocumentIds {
  const paperDocumentIds: string[] = [];
  const billDocumentIds: string[] = [];

  for (const result of results) {
    if (!result.success) continue;

    // Dual-doc results (GKV/Copayment)
    if ((result as any).paperDocumentId) {
      paperDocumentIds.push((result as any).paperDocumentId);
    }
    if ((result as any).billDocumentId) {
      billDocumentIds.push((result as any).billDocumentId);
    }

    // Single-doc results (Paper bill, Patient bill, etc.)
    // Use documentId as bill document when no explicit dual-doc fields
    if (result.documentId && !(result as any).paperDocumentId && !(result as any).billDocumentId) {
      billDocumentIds.push(result.documentId);
    }
  }

  return { paperDocumentIds, billDocumentIds };
}

// ============================================================================
// Download functions - shared across all flows
// ============================================================================

/**
 * Check if there are any documents to merge/download.
 */
export function hasAnyMergeableDocs(docIds: MergeDocumentIds): boolean {
  return docIds.paperDocumentIds.length > 0 || docIds.billDocumentIds.length > 0;
}

/**
 * Download a single merged payload as a file.
 * Converts base64 data to blob and triggers browser download.
 */
export function downloadMergedPayload(payload?: MergedDocumentPayloadDto): void {
  if (!payload?.base64Data) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const byteCharacters = window.atob(payload.base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: payload.contentType || "application/pdf" });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = payload.fileName || "merged.pdf";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Download both merged payloads from a merge response.
 * Handles both paperMerged and billMerged channels.
 */
export function autoDownloadMergedDocuments(
  mergeResult:
    | {
        paperMerged?: MergedDocumentPayloadDto;
        billMerged?: MergedDocumentPayloadDto;
      }
    | null
    | undefined,
): void {
  if (!mergeResult) return;
  downloadMergedPayload(mergeResult.paperMerged);
  downloadMergedPayload(mergeResult.billMerged);
}

/**
 * High-level helper: merge documents and auto-download results.
 * Use this for auto-download flows (GKV print).
 *
 * @param docIds - Document IDs to merge
 * @param mergeFn - Function to call the merge endpoint
 */
export async function mergeAndAutoDownload(
  docIds: MergeDocumentIds,
  mergeFn: (request: MergeDocumentIds) => Promise<{
    paperMerged?: MergedDocumentPayloadDto;
    billMerged?: MergedDocumentPayloadDto;
  }>,
): Promise<void> {
  if (!hasAnyMergeableDocs(docIds)) return;

  try {
    const mergeResult = await mergeFn(docIds);
    autoDownloadMergedDocuments(mergeResult);
  } catch (error) {
    console.error("Error merging/downloading PDFs:", error);
    // Don't throw - download failure shouldn't break the print flow
  }
}
